import React, { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AppBar, Box, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemText, Container, Avatar, Tooltip, Button, Divider } from '@mui/material';
import { configData } from '../../configData/configData';

const Header = () => {
    const { user, token, ruolo, logout } = useAuth();
    const navigate = useNavigate();

    // Elementi di navigazione del drawer
    const pages = [
        // Solo se non loggato
        { name: 'Login', url: '/login', visible: !token },
        { name: 'Registrati', url: '/registrami', visible: !token },

        // Visibile a TUTTI tranne ADMIN
        { name: 'Lista Form', url: '/listaForm', visible: token && ruolo !== 'ROLE_ADMIN' },

        // Solo ADMIN
        { name: 'Gestione Form Backoffice', url: '/listaFormBackoffice', visible: token && ruolo === 'ROLE_ADMIN' },
        { name: 'Lista dei Candidati', url: '/tab', visible: token && ruolo === 'ROLE_ADMIN' },        
        { name: 'Gestione Partner', url: '/partner', visible: token && ruolo === 'ROLE_ADMIN' },
        { name: 'Gestione Progetti', url: '/project', visible: token && ruolo === 'ROLE_ADMIN' },
        { name: 'Gestione Sotto-Progetti', url: '/subProject', visible: token && ruolo === 'ROLE_ADMIN' },
        //{ name: 'Esci', url: '/login', visible: token, action: logout }
    ];

    // Gestione del drawer laterale
    const [drawerOpen, setDrawerOpen] = useState(false);

    const toggleDrawer = (open) => () => {
        setDrawerOpen(open);
    };

    const handleMenuClick = (page) => {
        setDrawerOpen(false);
        navigate(page.url);
    };

    // State per il menu utente
    const [anchorElUser, setAnchorElUser] = React.useState(null);

    // Funzioni per aprire e chiudere il menu utente
    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    // Funzione per gestire il logout e chiudere il menù
    const handleLogout = () => {
        handleCloseUserMenu();
        logout();

        navigate('/login');
    };

    // Ottieni l'iniziale del nome utente
    const getUserInitial = () => {
        if (!user || !user.username) return 'U';
        return user.username.charAt(0).toUpperCase();
    };


    return (
      <AppBar position="static" style={{ backgroundColor: "#1d659c" }}>
        <Container maxWidth="false" disableGutters>
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            {/* Menu hamburger sempre visibile a sinistra */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title="Apri menù di navigazione">
                <IconButton
                  size="large"
                  aria-label="menu di navigazione"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={toggleDrawer(true)}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>

              {/* Pulsante home vicino al menù */}
              <Tooltip title="Vai alla Homepage">
                <IconButton
                  size="large"
                  color="inherit"
                  onClick={() => navigate("/homepage")}
                  sx={{ ml: 1 }}
                >
                  <HomeIcon />
                </IconButton>
              </Tooltip>

              <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
              >
                <Box sx={{ width: 250 }}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h6">Menu</Typography>
                    <IconButton onClick={toggleDrawer(false)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Divider />
                  <List>
                    {pages
                      .filter((page) => page.visible)
                      .map((page) => (
                        <ListItem
                          button
                          key={page.name}
                          onClick={() => handleMenuClick(page)}
                        >
                          <ListItemText primary={page.name} />
                        </ListItem>
                      ))}
                  </List>
                </Box>
              </Drawer>
            </Box>

            {/* Logo "Candidature" centrale */}
            <Typography
              variant="h5"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                fontFamily: "Ubuntu-Black",
                fontWeight: 700,
                color: "#a6cc38",
                textDecoration: "none",
                fontSize: { xs: '1rem', sm: '1.25rem' },
                whiteSpace: 'nowrap',
              }}
            >
              Candidature
            </Typography>

            {/* Avatar utente o pulsante login a destra*/}
            <Box sx={{ marginLeft: "auto" }}>
              {token ? (
                // Utente loggato: mostra avatar
                <Tooltip title="Apri dettagli del profilo">
                  <IconButton
                    onClick={handleOpenUserMenu}
                    size="small"
                    edge="end"
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "transparent",
                        color: "white",
                        border: "2px solid white",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                    >
                      {getUserInitial()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              ) : (
                // Utente non loggato: mostra pulsante Login o Registrati
                <>
                  {/* Desktop: Button normale */}
                  <Button
                    variant="outlined"
                    color="inherit"
                    sx={{
                      display: { xs: "none", sm: "inline-flex" },
                      borderColor: "white",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderColor: "white",
                      },
                    }}
                    onClick={() => navigate("/login")}
                  >
                    Login / Registrati
                  </Button>

                  {/* Mobile: IconButton con icona */}
                  <Tooltip title="Login">
                    <IconButton
                      sx={{
                        display: { xs: "inline-flex", sm: "none" },
                        color: "white",
                        border: "1px solid white",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                      size="small"
                      onClick={() => navigate("/login")}
                    >
                      <LoginIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              {/* Menu dropdown utente */}
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar-user"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <Box sx={{ px: 2, py: 1, width: 280, maxWidth: '90vw' }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">Dettagli utente</Typography>
                    <IconButton size="small" onClick={handleCloseUserMenu}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" fontWeight="bold">
                      {user?.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.email}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Ruolo: {ruolo}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{ mt: 1 }}
                  >
                    Logout
                  </Button>
                </Box>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    );
};

export default Header;